import React, { useState, useRef } from 'react';
// eslint-disable-next-line no-unused-vars
import { IoClose, IoImageOutline, IoSend, IoCloudUploadOutline, IoTrashOutline } from 'react-icons/io5';
import { db, storage } from '../firebaseConfig';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { useAuth } from '../context/AuthContext';
import AIAssistantButton from '../components/Shared/AIAssistantButton';
import './MuroScreen.css';

const CrearPostModal = ({ onClose, onSuccess }) => {
    const { currentUser, userData } = useAuth();
    const [titulo, setTitulo] = useState('');
    const [contenido, setContenido] = useState('');
    const [tipo, setTipo] = useState('aviso'); // aviso, venta, servicio, evento
    const [precio, setPrecio] = useState('');
    const [previewUrl, setPreviewUrl] = useState('');
    const [imagenUrl, setImagenUrl] = useState('');
    const [uploading, setUploading] = useState(false);
    const [loading, setLoading] = useState(false);

    const fileInputRef = useRef(null);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!titulo.trim() || !contenido.trim()) return;

        setLoading(true);
        try {
            await addDoc(collection(db, 'muro_posts'), {
                titulo,
                contenido,
                tipo,
                precio: tipo === 'venta' ? precio : null,
                imagenUrl: imagenUrl || null,
                usuarioId: currentUser?.uid || 'anonimo',
                usuarioNombre: userData?.nombre || 'Residente',
                usuarioUnidad: userData?.numeroCasa || '',
                condominioId: userData?.condominioId || '',
                fechaCreacion: serverTimestamp(),
                estatus: 'pendiente',
                likes: []
            });
            onSuccess();
            onClose();
        } catch (error) {
            console.error("Error creating post:", error);
            alert("Error al enviar publicación: " + error.message);
            setLoading(false);
        }
    };

    const [converting, setConverting] = useState(false);

    const handleFileChange = async (e) => {
        let file = e.target.files[0];
        if (!file) return;

        const isHEIC = file.name.toLowerCase().endsWith('.heic') || file.name.toLowerCase().endsWith('.heif');
        setUploading(true);

        try {
            if (isHEIC) {
                setConverting(true);
                console.log("Detectado formato HEIC. Convirtiendo...");
                if (!window.heic2any) {
                    await new Promise((resolve, reject) => {
                        const script = document.createElement('script');
                        script.src = 'https://unpkg.com/heic2any/dist/heic2any.js';
                        script.onload = resolve;
                        script.onerror = () => reject(new Error("No se pudo cargar el convertidor de imágenes."));
                        document.head.appendChild(script);
                    });
                }
                const blob = await window.heic2any({ blob: file, toType: 'image/jpeg', quality: 0.7 });
                file = new File([Array.isArray(blob) ? blob[0] : blob], file.name.replace(/\.(heic|heif)$/i, '.jpg'), { type: 'image/jpeg' });
                setConverting(false);
            }

            if (file.size > 5 * 1024 * 1024) {
                alert("La imagen es demasiado grande. Máximo 5MB.");
                setUploading(false);
                return;
            }

            // Preview local
            const reader = new FileReader();
            reader.onloadend = () => setPreviewUrl(reader.result);
            reader.readAsDataURL(file);

            // Subir a Firebase
            if (!currentUser) throw new Error("Debes iniciar sesión para subir imágenes.");
            const storagePath = `muro_uploads/${currentUser.uid}_${Date.now()}_${file.name.replace(/\s+/g, '_')}`;
            const storageRef = ref(storage, storagePath);
            
            console.log("Subiendo a path:", storagePath);
            const snapshot = await uploadBytes(storageRef, file);
            const downloadURL = await getDownloadURL(snapshot.ref);
            setImagenUrl(downloadURL);

        } catch (error) {
            console.error("Error en proceso de imagen:", error);
            if (isHEIC) {
                alert("No pudimos convertir tu foto de iPhone (.HEIC). Por favor intenta subirla como JPG o PNG.");
            } else {
                alert("Error al procesar la imagen: " + error.message);
            }
        } finally {
            setUploading(false);
        }
    };

    return (
        <div className="premium-modal-overlay">
            <div className="premium-modal">
                <div className="modal-header">
                    <h2>Nueva Publicación</h2>
                    <button className="modal-close-btn" onClick={onClose}>
                        <IoClose />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="modal-form">
                    <div className="form-group">
                        <label>Título</label>
                        <input
                            type="text"
                            className="form-input"
                            value={titulo}
                            onChange={e => setTitulo(e.target.value)}
                            placeholder="Ej: Vendo Playstation 5"
                            required
                        />
                    </div>

                    <div className="form-group">
                        <label>Tipo</label>
                        <div className="chip-selector">
                            {['aviso', 'venta', 'servicio', 'evento'].map(t => (
                                <button
                                    key={t}
                                    type="button"
                                    className={`chip ${tipo === t ? 'active' : ''}`}
                                    onClick={() => setTipo(t)}
                                >
                                    {t.charAt(0).toUpperCase() + t.slice(1)}
                                </button>
                            ))}
                        </div>
                    </div>

                    {tipo === 'venta' && (
                        <div className="form-group slide-down">
                            <label>Precio</label>
                            <div style={{ position: 'relative' }}>
                                <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#6b7280', fontWeight: 600 }}>$</span>
                                <input
                                    type="number"
                                    className="form-input"
                                    style={{ paddingLeft: '28px' }}
                                    value={precio}
                                    onChange={e => setPrecio(e.target.value)}
                                    placeholder="0.00"
                                    required={tipo === 'venta'}
                                />
                            </div>
                        </div>
                    )}

                    <div className="form-group">
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                            <label style={{ margin: 0 }}>Contenido</label>
                            <AIAssistantButton 
                                textoOriginal={contenido}
                                onSugerenciaAceptada={(nuevoTexto) => setContenido(nuevoTexto)}
                                contexto="muro"
                            />
                        </div>
                        <textarea
                            className="form-input"
                            rows="4"
                            value={contenido}
                            onChange={e => setContenido(e.target.value)}
                            placeholder="Describe tu publicación..."
                            required
                        />
                    </div>

                    <div className="form-group">
                        <label>Imagen (Opcional)</label>
                        <input
                            type="file"
                            ref={fileInputRef}
                            onChange={handleFileChange}
                            accept="image/*"
                            hidden
                        />

                        <div
                            className="image-upload-box"
                            onClick={() => !previewUrl && fileInputRef.current.click()}
                            style={{
                                border: '2px dashed #4f46e5',
                                background: '#eef2ff',
                                borderRadius: '12px',
                                padding: '20px',
                                textAlign: 'center',
                                cursor: previewUrl ? 'default' : 'pointer',
                                color: '#4f46e5',
                                transition: 'all 0.2s',
                                position: 'relative'
                            }}
                        >
                            {uploading ? (
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
                                    <div className="spinner-mini"></div>
                                    <p>{converting ? 'Optimizando foto de iPhone...' : 'Subiendo imagen...'}</p>
                                    {previewUrl && (
                                        <img
                                            src={previewUrl}
                                            alt="Uploading preview"
                                            style={{ maxWidth: '100%', maxHeight: '100px', borderRadius: '8px', opacity: 0.5 }}
                                        />
                                    )}
                                </div>
                            ) : previewUrl ? (
                                <div style={{ position: 'relative', display: 'inline-block' }}>
                                    <img
                                        src={previewUrl}
                                        alt="Preview"
                                        style={{ maxWidth: '100%', maxHeight: '150px', borderRadius: '16px', display: 'block', border: '1px solid #e5e7eb' }}
                                        onError={(e) => {
                                            console.error("Error cargando preview local (FileReader):", e);
                                        }}
                                    />
                                    <button
                                        type="button"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setPreviewUrl('');
                                            setImagenUrl('');
                                            if (fileInputRef.current) fileInputRef.current.value = '';
                                        }}
                                        title="Quitar imagen"
                                        style={{
                                            position: 'absolute', top: 8, right: 8,
                                            background: 'rgba(15, 23, 42, 0.7)', color: 'white',
                                            border: 'none', borderRadius: '50%',
                                            width: 28, height: 28, cursor: 'pointer',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            backdropFilter: 'blur(4px)',
                                            transition: 'transform 0.2s'
                                        }}
                                        onMouseOver={e => e.currentTarget.style.transform = 'scale(1.1)'}
                                        onMouseOut={e => e.currentTarget.style.transform = 'scale(1)'}
                                    >
                                        <IoClose size={18} />
                                    </button>
                                </div>
                            ) : (
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5 }}>
                                    <IoCloudUploadOutline size={32} />
                                    <span style={{ fontWeight: 600 }}>Clic para subir imagen</span>
                                    <span style={{ fontSize: '0.8rem', opacity: 0.8 }}>JPG, PNG (Max 5MB)</span>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="modal-actions" style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
                        <button
                            type="button"
                            onClick={onClose}
                            style={{
                                flex: 1, padding: '12px', borderRadius: '12px', border: '1px solid #d1d5db',
                                background: 'white', color: '#374151', fontWeight: 600, cursor: 'pointer'
                            }}
                        >
                            Cancelar
                        </button>
                        <button
                            type="submit"
                            disabled={loading || uploading}
                            style={{
                                flex: 1, padding: '12px', borderRadius: '12px', border: 'none',
                                background: 'linear-gradient(135deg, #6366f1 0%, #4338ca 100%)',
                                color: 'white', fontWeight: 600, cursor: 'pointer',
                                opacity: (loading || uploading) ? 0.7 : 1,
                                boxShadow: '0 4px 12px rgba(79, 70, 229, 0.3)',
                                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8
                            }}
                        >
                            {loading ? 'Enviando...' : <><IoSend /> Enviar a Revisión</>}
                        </button>
                    </div>
                </form>
            </div>
            <style>{`
                .premium-modal-overlay {
                    background: rgba(0, 0, 0, 0.6);
                    backdrop-filter: blur(4px);
                    position: fixed;
                    top: 0; left: 0; right: 0; bottom: 0;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    z-index: 10000;
                    overflow-y: auto;
                    padding: 20px 0;
                }
                .premium-modal {
                    background: white;
                    border-radius: 24px;
                    width: 90%;
                    max-width: 500px;
                    padding: 24px;
                    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
                    position: relative;
                    margin: auto;
                }
                @media (max-width: 600px) {
                    .premium-modal-overlay {
                        align-items: flex-start;
                    }
                    .premium-modal {
                        padding: 20px;
                        width: 95%;
                        margin-top: 20px;
                        margin-bottom: 20px;
                    }
                }
                .chip-selector { display: flex; gap: 8px; flex-wrap: wrap; margin-top: 8px; }
                .chip {
                    padding: 8px 16px;
                    border-radius: 20px;
                    border: 1px solid #e5e7eb;
                    background: white;
                    cursor: pointer;
                    transition: all 0.2s;
                    font-weight: 500;
                    color: #4b5563;
                }
                .chip:hover { background: #f9fafb; }
                .chip.active {
                    background: #eff6ff;
                    color: #4f46e5;
                    border-color: #4f46e5;
                    font-weight: 600;
                }
                .form-input {
                    padding: 12px;
                    border: 1px solid #d1d5db;
                    border-radius: 12px;
                    font-size: 0.95rem;
                    width: 100%;
                    box-sizing: border-box;
                    transition: border-color 0.2s;
                }
                .form-input:focus {
                    border-color: #6366f1;
                    outline: none;
                    box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
                }
                .slide-down {
                    animation: slideDown 0.3s ease-out;
                }
                @keyframes slideDown {
                    from { opacity: 0; transform: translateY(-10px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                .spinner-mini {
                    width: 20px;
                    height: 20px;
                    border: 2px solid #eef2ff;
                    border-top: 2px solid #6366f1;
                    border-radius: 50%;
                    animation: spin 0.8s linear infinite;
                }
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
            `}</style>
        </div>
    );
};

export default CrearPostModal;
